import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Chip,
  Paper,
  Avatar,
  Divider,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Close, LocationOn, AttachMoney, Home } from '@mui/icons-material';
import api from '../../utils/api';
import { Client, Property } from '../../types';
import PropertyDetailsDialog from '../Properties/PropertyDetailsDialog';

interface ClientMatchingPropertiesDialogProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
}

const ClientMatchingPropertiesDialog: React.FC<ClientMatchingPropertiesDialogProps> = ({
  open,
  onClose,
  client,
}) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [propertyDetailsOpen, setPropertyDetailsOpen] = useState(false);

  const loadMatchingProperties = useCallback(async () => {
    if (!client) return;

    setLoading(true);
    try {
      // Build query parameters based on client preferences
      const params: any = {
        'status[]': ['AVAILABLE', 'APPROVED'],
      };

      // Add budget range filters
      if (client.minBudget) {
        params.minPrice = client.minBudget;
      }
      if (client.maxBudget) {
        params.maxPrice = client.maxBudget;
      }

      // Add property types filter as array
      if (client.propertyTypes && client.propertyTypes.length > 0) {
        params['propertyTypes[]'] = client.propertyTypes;
      }

      // Add bedrooms filter
      if (client.bedrooms) {
        params.bedrooms = client.bedrooms;
      }

      // Add bathrooms filter
      if (client.bathrooms) {
        params.bathrooms = client.bathrooms;
      }

      // Add area filter
      if (client.area) {
        params.area = client.area;
      }

      // Add building age filter
      // if (client.buildingAge) { params.buildingAge = client.buildingAge; }

      // Add payment method filter
      if (client.paymentMethod) {
        params.paymentMethod = client.paymentMethod;
      }

      // Add transaction type filter
      if (client.transactionType) {
        params.transactionType = client.transactionType;
      }

      // Add location filters
      if (client.governorate) {
        params['governorate[]'] = Array.isArray(client.governorate) ? client.governorate : [client.governorate];
      }
      if (client.district) {
        params['district[]'] = Array.isArray(client.district) ? client.district : [client.district];
      }
      // if (client.town) { params.town = client.town; }

      const response = await api.get('/properties', { params });
      setProperties(response.data);
    } catch (error) {
      console.error('Error loading properties:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (open && client) {
      loadMatchingProperties();
    }
  }, [open, client, loadMatchingProperties]);

  const handlePropertyClick = (property: Property) => {
    setSelectedProperty(property);
    setPropertyDetailsOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'success';
      case 'APPROVED':
        return 'info';
      case 'PENDING':
        return 'warning';
      case 'SOLD':
        return 'error';
      case 'RENTED':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Property',
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5, 
            py: 1,
            cursor: 'pointer',
            '&:hover': {
              '& .property-title': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
            },
          }}
          onClick={() => handlePropertyClick(params.row)}
        >
          {params.row.images && params.row.images.length > 0 ? (
            <Avatar
              src={params.row.images[0]}
              variant="rounded"
              sx={{ width: 56, height: 56 }}
            />
          ) : (
            <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'primary.light' }}>
              <Home />
            </Avatar>
          )}
          <Box>
            <Typography className="property-title" variant="body2" fontWeight={600} noWrap>
              {params.row.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn sx={{ fontSize: 14 }} />
              {params.row.city}, {params.row.country}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'propertyType',
      headerName: 'Type',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          label={params.row.propertyType}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AttachMoney sx={{ fontSize: 18, color: 'success.main' }} />
          <Typography variant="body2" fontWeight={600} color="success.main">
            {params.row.price.toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'size',
      headerName: 'Size',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => params.row.size ? `${params.row.size} m²` : 'N/A',
    },
    {
      field: 'bedrooms',
      headerName: 'Beds',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.row.bedrooms || 'N/A',
    },
    {
      field: 'bathrooms',
      headerName: 'Baths',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.row.bathrooms || 'N/A',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={getStatusColor(params.row.status)}
        />
      ),
    },
  ];

  const getBudgetDisplay = () => {
    if (!client) return 'Any';
    
    if (client.minBudget && client.maxBudget) {
      return `$${client.minBudget.toLocaleString()} - $${client.maxBudget.toLocaleString()}`;
    } else if (client.minBudget) {
      return `From $${client.minBudget.toLocaleString()}`;
    } else if (client.maxBudget) {
      return `Up to $${client.maxBudget.toLocaleString()}`;
    }
    return 'Any';
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Matching Properties
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {client && `${client.firstName} ${client.lastName}`}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Client Preferences Summary */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            bgcolor: 'background.default',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
            CLIENT PREFERENCES
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Budget Range
              </Typography>
              <Typography variant="body1" fontWeight={600} color="primary.main">
                {getBudgetDisplay()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Property Types
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {client?.propertyTypes && client.propertyTypes.length > 0 ? (
                  client.propertyTypes.map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    All Types
                  </Typography>
                )}
              </Box>
            </Box>
            {client?.bedrooms && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Bedrooms
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.bedrooms}
                </Typography>
              </Box>
            )}
            {client?.bathrooms && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Bathrooms
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.bathrooms}
                </Typography>
              </Box>
            )}
            {client?.area && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Area
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.area} m²
                </Typography>
              </Box>
            )}
            {client?.buildingAge && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Building Age
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.buildingAge} years
                </Typography>
              </Box>
            )}
            {client?.paymentMethod && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Payment Method
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.paymentMethod}
                </Typography>
              </Box>
            )}
            {client?.transactionType && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Transaction Type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.transactionType}
                </Typography>
              </Box>
            )}
            {client?.governorate && (Array.isArray(client.governorate) ? client.governorate.length > 0 : true) && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Governorate
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {(Array.isArray(client.governorate) ? client.governorate : [client.governorate]).map((g) => (
                    <Chip key={g} label={g} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
            {client?.district && (Array.isArray(client.district) ? client.district.length > 0 : true) && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  District
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {(Array.isArray(client.district) ? client.district : [client.district]).map((d) => (
                    <Chip key={d} label={d} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
            {client?.town && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Town
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {client.town}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Matching Properties
              </Typography>
              <Typography variant="body1" fontWeight={600} color="success.main">
                {properties.length} {properties.length === 1 ? 'Property' : 'Properties'}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Properties Table */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <DataGrid
            rows={properties}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: 'none',
              minWidth: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                minHeight: '56px !important',
                maxHeight: '56px !important',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#f8f9fa',
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                padding: '0 8px',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  overflow: 'visible',
                  whiteSpace: 'normal',
                  lineHeight: '1.2',
                },
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                padding: '8px 0',
              },
              '& .MuiDataGrid-cell': {
                borderColor: '#f0f0f0',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '4px', sm: '8px' },
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8f9ff',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row': {
                minHeight: { xs: '70px', sm: '80px' },
                maxHeight: 'fit-content',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto',
              },
            }}
          />
        </Paper>

        {properties.length === 0 && !loading && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3,
            }}
          >
            <Home sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Matching Properties Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are currently no properties that match this client's preferences.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <PropertyDetailsDialog
        open={propertyDetailsOpen}
        onClose={() => {
          setPropertyDetailsOpen(false);
          setSelectedProperty(null);
        }}
        property={selectedProperty}
      />
    </Dialog>
  );
};

export default ClientMatchingPropertiesDialog;
